import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  Button,
  IconButton,
  Typography,
  AppBar,
  Toolbar,
  Stack,
  Box,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  Chip,
  TextField,
  Checkbox,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Formik, Form } from "formik";
import ReportCategories from "./ReportCategories";
import ReportsViewer from "./ReportsViewer";
import MailCompose from "./MailCompose";
import { postReportSchedule } from "../API/backend";
import { handleSessionOut, Loader } from "../HelperFunctions/HelperFunctions";
import { enqueueSnackbar } from "notistack";
import { useLoaderData, useSearchParams } from "react-router-dom";
import { getYesterdayDate } from "../HelperFunctions/HelperFunctions";

const SchedulerDialog = ({
  open,
  onClose,
  onSubmitSuccess,
  data = {},
  isEdit = false,
  userName,
  editCustomData,
}) => {
  const {
    privileges = {},
    clientID,
    clientName,
    UID,
    userPreferences,
    token,
  } = useLoaderData();

  const [slide, setSlide] = useState(1);
  const [loading, setLoading] = useState(false);

  const subject = (reportBookName = "") => {
    const date = getYesterdayDate();
    const day = new Date(date);
    const longDate = day.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    });
    const book =
      (reportBookName || data?.schedules?.report_book || "").trim() ||
      "Scheduled Reports";
    return `${book} ${longDate}`;
  };

  const initialValues = {
    client_id: clientID,
    client_name: clientName,
    user_id: UID,
    user_name: userName || "",
    report_name: data.reportName || [],
    is_corp_legal: userPreferences?.IsShowCorpLegalName,
    reportBookName: data?.schedules?.report_book || "",
    categoryOfReport:
      data?.schedules?.report_category || "property_financials_and_accountant",
    reportBookType: data?.schedules?.report_type || "custom_reports",
    reportFrequency: data?.schedules?.report_frequency || "",
    reportFrom: data?.schedules?.report_from || "",
    checkedItems: data?.schedules?.checked_items || {},
    reviewReportBookType: data?.schedules?.report_type || "custom_reports",
    reviewReportFrequency: data?.schedules?.report_frequency || "",
    corp_management_groups: data?.schedules?.corp_management_groups || "ALL",
    reportsViewerData: data?.schedules?.reports_viewer_data || {},
    selected_reports: data?.schedules?.selected_reports || [],
    selected_corporations: data?.schedules?.corporations_selected || [],
    customization:
      editCustomData?.customizations ||
      data?.schedules?.customization ||
      {},
    roles: data?.schedules?.roles || "all",
    users: data?.schedules?.users || [],
    users_info: data?.schedules?.users_info || [],
    frequency: {
      mail_frequency: "daily",
      // day: data?.schedules?.schedule?.day || "",
      day: "",
      time: data?.schedules?.schedule?.time || "",
      time_zone: data?.schedules?.schedule?.time_zone || "",
    },
    mail_compose: {
      to: data?.schedules?.users || [],
      cc: Array.isArray(data?.schedules?.mail_compose?.cc)
        ? data.schedules.mail_compose.cc.join(";")
        : data?.schedules?.mail_compose?.cc || "",
      subject:
        data?.schedules?.mail_compose?.subject ||
        subject(data?.schedules?.report_book || ""),
      mail_body: data?.schedules?.mail_compose?.body || "",
    },

    schedule_id: data?.schedules?.schedule_id || "",
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      delete values.cropCount;
      // Keep only checked report customizations in payload
      const checkedItems = values.checkedItems || {};
      const filteredCustomization = Object.fromEntries(
        Object.entries(values.customization || {}).filter(
          ([key]) => key.includes("Report") && checkedItems[key],
        ),
      );

      // Ensure Payment Log is present when checked (defaults if never customized)
      if (
        checkedItems["Payment Log Report"] &&
        !filteredCustomization["Payment Log Report"]
      ) {
        filteredCustomization["Payment Log Report"] = {
          bankAccount: null,
          paymentMethod: null,
          transaction: null,
          exportType: null,
          reportView: "MMDDYYYY",
        };
      }

      values.customization = filteredCustomization;
      const checkedNames = Object.keys(checkedItems).filter(
        (key) => checkedItems[key] && key.includes("Report"),
      );
      if (checkedNames.length > 0) {
        values.report_name = checkedNames;
      }
      const saveReportSchedule = await postReportSchedule(values);
      onClose();
      setSlide(slide - 2);
      await onSubmitSuccess();
      setLoading(false);
    } catch (error) {
      if (error.status === 401) {
        await handleSessionOut();
      }
      setLoading(false);
      enqueueSnackbar("Error while Scheduling", { variant: "error" });
    }
  };

  return (
    <Dialog open={open} fullScreen>
      <AppBar
        sx={{ position: "relative", bgcolor: "#d7d7d7", height: 45 }}
        elevation={0}
      >
        <Toolbar
          sx={{ height: 45, minHeight: "45px !important", padding: "0 8px" }}
        >
          <IconButton
            size="small"
            edge="start"
            color="inherit"
            // onClick={onClose}
            onClick={() => {
              setSlide(1);
              // resetForm();
              onClose();
            }}
            aria-label="close"
            style={{ backgroundColor: "#686868" }}
            disableRipple
          >
            <CloseIcon sx={{ fontSize: "15px" }} />
          </IconButton>
          <Typography
            sx={{
              ml: 2,
              flex: 1,
              color: "#1f1f1f",
            }}
            component="div"
            fontWeight={600}
            fontSize={"16px"}
          >
            Report Book
          </Typography>
        </Toolbar>
      </AppBar>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize={false}
      >
        {({ values, handleChange, setFieldValue }) => (
          <Form>
            {slide === 1 && (
              <ReportCategories
                slide={slide}
                setSlide={setSlide}
                onClose={onClose}
              />
            )}
            {slide === 2 && (
              <ReportsViewer
                slide={slide}
                setSlide={setSlide}
                onClose={onClose}
                isEdit={isEdit}
                editCustomData={editCustomData}
              />
            )}
            {slide === 3 && (
              <MailCompose
                slide={slide}
                setSlide={setSlide}
                onClose={onClose}
              />
            )}
          </Form>
        )}
      </Formik>
      <Loader loading={loading} />
    </Dialog>
  );
};

export default SchedulerDialog;
